/**
 * The zustand-`persist` half of per-account namespacing.
 *
 * A persisted store has TWO copies of its data: the localStorage key and the
 * in-memory state rehydrated from it. Namespacing only the key fixes half the
 * problem — the state loaded while account A was signed in stays in memory
 * across a switch to B, so B reads A's certificates out of the live store even
 * though B could never name A's key. These helpers close both halves:
 *
 *   accountScopedStorage(suffix)   the key is rebuilt on EVERY read/write from
 *                                  the current namespace, so a store can never
 *                                  cache a stale key name
 *   rehydrateOnAccountChange(...)  on a switch, the persisted slice is replaced
 *                                  wholesale by whatever the incoming account
 *                                  has — defaults where it has nothing
 *
 * "Defaults where it has nothing" is the load-bearing half. zustand's own
 * `persist.rehydrate()` merges what it finds and leaves state untouched when
 * the key is ABSENT — which is exactly the case where B has never used this
 * device, so "B has nothing stored" would silently mean "B keeps looking at
 * A's". Hence the read and the reset happen in ONE setState: resetting first
 * and rehydrating second would persist the empty state over the incoming
 * account's own key before it had been read.
 */
import { apibooKey, onStorageNamespaceChange } from './storageNamespace';

/**
 * A zustand StateStorage that ignores the store's `name` and keys off the
 * current account instead: `apiboo-<accountId>-<suffix>`.
 */
export function accountScopedStorage(suffix) {
  return {
    getItem: () => {
      try {
        return localStorage.getItem(apibooKey(suffix));
      } catch {
        return null;
      }
    },
    setItem: (_name, value) => {
      try {
        localStorage.setItem(apibooKey(suffix), value);
      } catch {
        // ignore (quota / disabled storage)
      }
    },
    removeItem: () => {
      try {
        localStorage.removeItem(apibooKey(suffix));
      } catch {
        // ignore
      }
    },
  };
}

/**
 * Re-point a persisted store at the incoming account on every switch.
 *
 * @param store       the zustand store (must use persist())
 * @param emptySlice  the persisted fields at their "never seen this device"
 *                    defaults. Shallow-merged, so non-persisted fields (live
 *                    handles, registered manifests, ...) are left alone.
 * @param storage     the same StateStorage the store persists through — read
 *                    directly so the incoming values and the defaults land in
 *                    a single set().
 * @returns {() => void} unsubscribe
 */
export function rehydrateOnAccountChange(store, emptySlice, storage) {
  return onStorageNamespaceChange(() => {
    let incoming = null;
    try {
      const raw = storage.getItem();
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed.state === 'object') incoming = parsed.state;
    } catch (err) {
      console.warn('[accountScopedPersist] unreadable persisted state:', err);
    }
    const defaults = typeof emptySlice === 'function' ? emptySlice() : emptySlice;
    store.setState({ ...defaults, ...(incoming || {}) });
  });
}
